<template>
  <div>
      <button @click="addFromTeamApplication()" class="btn btn-sm btn-info">
        <span>{{ buttonText }}</span></button>
    <modal :name="`add-team-from-application-${uuid}`" :adaptive="true" height="auto" :minWidth="400" width="80%">
      <div style="min-height: 400px" class="overflow-y-auto">
        <ntm-block :title="$t('schedule.team.add_teams_from_applications')" :collapsable="false">
          <template #options>
            <button type="button" class="btn btn-sm btn-light" @click="$modal.hide(`add-team-from-application-${uuid}`)" v-tooltip="$t('close')">
              <i class="fa fa-times"></i>
            </button>
          </template>
          <multiselect
            :allow-empty="false"
            track-by="uuid"
            class="mb-2"
            :custom-label="(option) => option.name"
            :placeholder="$tc('applicationWindow.name', 1)"
            v-model="teamApplicationFilters.applicationWindow"
            :options="applicationWindowOptions"
          />
          <multiselect
            :disabled="teamApplicationFilters.applicationWindow === null"
            class="mb-2"
            :multiple="false"
            :taggable="false"
            :placeholder="$tc('category.name', 1)"
            :close-on-select="true"
            v-model="teamApplicationFilters.category"
            :options="categoryOptions"
          />
          <div class="custom-control custom-checkbox custom-checkbox-rounded-circle custom-control-lg custom-control-danger mb-1">
            <input type="checkbox" v-model="teamApplicationFilters.isImported" class="custom-control-input" id="is-imported">
            <label class="custom-control-label" for="is-imported" v-text="$t('statusBadge.imported')"></label>
          </div>

          <div v-if="teamApplications.length" class="table-wrapper table-responsive">
            <table class="table table-bordered table-striped table-vcenter">
              <thead>
              <tr>
                <th class="">{{ $t('formly.fields.name') }}</th>
                <th class="">{{ $t('formly.fields.email') }}</th>
                <th></th>
                <th class="text-center" style="width: 100px;">{{ $tc('action.name', 2) }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(teamApplication, index) in teamApplications" :key="index">
                <td class="">
                  <span class="font-size-h6 font-w600">{{ teamApplication.teamName || teamApplication.managerName }}</span><br>
                  <span class="fs-xs fw-semibold d-inline-block badge px-2 py-1 text-uppercase mr-1"
                        :class="teamApplication.type === 'TEAM' ? 'badge-primary' : 'badge-info'">
                    {{ $t(`select.options.${teamApplication.type}`) }}
                  </span>
                  <span class="fs-xs fw-semibold d-inline-block badge px-2 py-1 text-uppercase" :class="getStatusClass(teamApplication.status)">
                    {{ $t(`select.options.${teamApplication.status}`) }}
                  </span>
                </td>
                <td class=""><span>{{ teamApplication.managerEmail }}</span><br> <span class="font-size-sm">({{ teamApplication.managerName }})</span></td>
                <td>
                  <span>
                    {{ $tc('coach.name', 2) }}: {{ teamApplication.coachesCount }}<br>
                    {{ $tc('player.name', 2) }}: {{ teamApplication.playersCount }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="btn-group">
                    <button type="button" class="btn btn-sm btn-success" @click="addTeamApplicationToSchedule(teamApplication)" v-tooltip="$t('tooltip.addTeamToSchedule')"><i class="fa fa-plus"></i></button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </ntm-block>
      </div>
    </modal>
  </div>
</template>

<script>

import Multiselect from 'vue-multiselect'
import ApplicationWindowService from '@/services/applicationWindow.service'
import { v4 as uuidv4 } from 'uuid'

export default {
  components: { Multiselect },
  props: {
    closeAfterSelect: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String,
      default: 'Add teams from applications'
    },
    types: {
      type: Array,
      default: () => ['TEAM']
    }
  },
  data () {
    return {
      uuid: uuidv4(),
      loading: false,
      applicationWindowOptions: [],
      categoryOptions: [],
      teamApplicationFilters: {
        applicationWindow: null,
        category: null,
        isImported: false
      },
      teamApplications: []
    }
  },
  methods: {
    getStatusClass (status) {
      return {
        'badge-success': status === 'APPROVED',
        'badge-danger': status === 'REJECTED',
        'badge-warning': status === 'PENDING'
      }
    },
    addTeamApplicationToSchedule (teamApplication) {
      this.$emit('applicationSelected', {
        applicationWindow: this.teamApplicationFilters.applicationWindow,
        teamApplication: teamApplication
      })

      if (!this.teamApplicationFilters.isImported) {
        this.teamApplications.splice(this.teamApplications.indexOf(teamApplication), 1)
      }

      if (this.closeAfterSelect) {
        this.$modal.hide(`add-team-from-application-${this.uuid}`)
      }
    },
    addFromTeamApplication () {
      ApplicationWindowService.index().then((response) => {
        this.applicationWindowOptions = response.data.data.map((item) => {
          return {
            uuid: item.uuid,
            name: item.name,
            categories: item.categories
          }
        })

        const storedApplicationWindow = localStorage.getItem('teamApplicationWindow')

        if (storedApplicationWindow) {
          const parsedWindow = JSON.parse(storedApplicationWindow)
          this.teamApplicationFilters.applicationWindow = this.applicationWindowOptions.find(item => item.uuid === parsedWindow.uuid) || null
        } else {
          this.teamApplicationFilters.applicationWindow = null
        }

        const storedIsImported = localStorage.getItem('teamApplicationIsImported')
        this.teamApplicationFilters.isImported = storedIsImported === 'true'
      })

      this.$modal.show(`add-team-from-application-${this.uuid}`)
    },
    fetchTeamApplications () {
      if (this.teamApplicationFilters.applicationWindow) {
        this.loading = true
        ApplicationWindowService.teamApplications(this.teamApplicationFilters.applicationWindow.uuid, {
          categories: this.teamApplicationFilters.category ? [this.teamApplicationFilters.category] : [],
          statuses: ['APPROVED'],
          types: this.types,
          isImported: this.teamApplicationFilters.isImported,
          page: 1
        }).then((response) => {
          this.teamApplications = response.data.data
        }).catch((err) => {
          console.error(err)
        }).finally(() => {
          this.loading = false
        })
      } else {
        this.teamApplications = []
      }
    }
  },
  watch: {
    'teamApplicationFilters.applicationWindow': function (value) {
      localStorage.setItem('teamApplicationWindow', JSON.stringify(value))

      if (value) {
        this.categoryOptions = value.categories
        const storedCategory = localStorage.getItem('teamApplicationCategory')

        if (storedCategory) {
          const parsedCategory = JSON.parse(storedCategory)
          this.teamApplicationFilters.category = this.teamApplicationFilters.applicationWindow?.categories.find(item => item === parsedCategory) || null
        } else {
          this.teamApplicationFilters.category = null
        }
        this.teamApplications = []
      } else {
        this.teamApplicationFilters.category = null
      }
    },
    'teamApplicationFilters.category': function (value) {
      localStorage.setItem('teamApplicationCategory', JSON.stringify(value))
      if (!this.teamApplicationFilters.category) {
        this.teamApplications = []
        return
      }

      this.fetchTeamApplications()
    },
    'teamApplicationFilters.isImported': function (value) {
      localStorage.setItem('teamApplicationIsImported', value)
      if (!this.teamApplicationFilters.category) {
        return
      }

      this.fetchTeamApplications()
    }
  }
}
</script>
